<template>
    <Card pageTitle="Receive Transfer">
        <AllowPermission :permission="PERMISSION_CODES.CMS_EDIT_TRANSFER">
            <div class="mb-3">
                <Button noMargin @click="navigateToTransferDetails"> 
                    <i class="fas fa-chevron-left"></i> Back To Transfer Details 
                </Button>
            </div>

            <div class="mb-4">
                <strong>Receive Transfer {{ stockTransfer && stockTransfer.name }}</strong>
            </div>
            
            <div>
                <CustomTable>
                    <template #head>
                        <tr>
                            <th>Item</th>
                            <th>SKU</th>
                            <th>Transfer Quantity</th>
                            <th>Received Previously</th>
                            <th>Received Quantity</th>
                            <th>Unit</th>
                            <th>Actions</th>
                        </tr>
                    </template>
                    <template #body>
                        <tr v-for="transferProduct of transferProducts" :key="transferProduct.id">
                            <td>
                                {{ transferProduct.product.name }}
                            </td>
                            <td>
                                {{ transferProduct.product.supplier_sku }}
                            </td>
                            <td>
                                {{ transferProduct.transferred_qty }}
                            </td>
                            <td>
                                {{ transferProduct.received_qty }}
                            </td>
                            <td>
                                <CustomInput
                                    small
                                    v-model="transferProduct.received_qty_new"
                                    :placeholder="`Max ${getRemainingQty(transferProduct)} Qty`"
                                    type="number"
                                />
                            </td>
                            <td>Single Unit</td>
                            <td>
                                <Button 
                                    small 
                                    noMargin 
                                    @click="receiveRemaining(transferProduct)"
                                    :disabled="getRemainingQty(transferProduct) <= 0"
                                >
                                    Received
                                </Button>
                            </td>
                        </tr>
                    </template>
                </CustomTable>
                <div v-if="loading" class="text-center my-3">Please wait...</div>
                <div v-else-if="!transferProducts.length" class="text-center my-3">No Products Added</div>
                
                <div class="d-flex justify-content-end mt-4" style="gap: 15px">
                    <Button @click="navigateToTransferDetails">Cancel</Button>
                    <Button @click="updateReceivedQty">{{ receivingProducts ? "Please wait..." : "Receive Products" }}</Button>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import CustomInput from "../../../components/crud/custom-input.vue"
import SelectDropdown from "../../../components/crud/SelectDropdown.vue"
import CustomTable from "../../../components/layout/CustomTable.vue"
import Button from "../../../components/UI/Button.vue"
import Card from "../../../components/UI/Card.vue"
import { errMsg } from "../../../utils/common"
import { http } from "../../../utils/http"
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../../store/permission"

export default {
    components: { AllowPermission, SelectDropdown, Button, CustomInput, CustomTable, Card },
    data() {
        return {
            stockTransfer: "",
            stock_transfer_status: "",
            transferProducts: "",
            loading: false,
            receivingProducts: false,
            PERMISSION_CODES,
        }
    },
    computed: {
        ...mapState("misc", ["warehouseRooms", "warehouses"]),
        ...mapState("auth", ["user"]),
    },
    mounted() {
        this.fetchTransferData()
    },
    methods: {
        navigateToTransferDetails() {
            console.log("navigate to transfer details")
            this.$router.push(`/stock-transfers/${this.$route.params.id}`)
        },
        
        getRemainingQty(transferProduct) {
            return Number(transferProduct.transferred_qty) - Number(transferProduct.received_qty)
        },
        
        receiveRemaining(transferProduct) {
            const remainingQty = this.getRemainingQty(transferProduct)
            if (remainingQty > 0) {
                this.$set(transferProduct, 'received_qty_new', remainingQty)
                this.$toast.success(`Set to receive ${remainingQty} units of ${transferProduct.product.name}`)
            }
        },

        async fetchTransferData() {
            this.loading = true
            try {
                const res = await http.get(`/stock-transfers/${this.$route.params.id}`)
                this.stockTransfer = res.data.result
                this.stock_transfer_status = this.stockTransfer.stock_transfer_status
                this.transferProducts = res.data.products
                
                this.transferProducts.forEach(product => {
                    this.$set(product, 'received_qty_new', 0)
                })
            } catch (err) {
                console.log("error in transferring stock", err)
            }
            this.loading = false
        },
        
        async updateReceivedQty() {
            if (this.receivingProducts) return

            for (let transferProduct of this.transferProducts) {
                const totalReceived = Number(transferProduct.received_qty) + Number(transferProduct.received_qty_new || 0)
                const totalLeft = Number(transferProduct.transferred_qty) - Number(transferProduct.received_qty)
                transferProduct.totalReceived = totalReceived

                if (transferProduct.transferred_qty < totalReceived) {
                    return this.$toast.error(`Max ${totalLeft} received quantity allowed for item: ${transferProduct.product.name}`)
                }
            }

            this.receivingProducts = true
            try {
                await http.patch(`/stock-transfers/${this.$route.params.id}/receive`, {
                    receivedProducts: this.transferProducts.map((item) => ({
                        id: item.id,
                        received_qty: item.totalReceived || 0,
                    })),
                    received_by_user_id: this.user?.id,
                    received_by_user_name: this.user?.full_name || `${this.user?.first_name || ''} ${this.user?.last_name || ''}`.trim(),
                })
                const allProductsFullyReceived = this.transferProducts.every(product => product.totalReceived >= Number(product.transferred_qty))

                if (allProductsFullyReceived) {
                    // Automatically complete the transfer
                    try {
                        await http.patch(`/stock-transfers/${this.$route.params.id}`, {
                            stock_transfer_status: 'COMPLETED',
                            modified_by_user_id: this.user?.id,
                            modified_by: this.user?.full_name || `${this.user?.first_name || ''} ${this.user?.last_name || ''}`.trim(),
                        })
                        this.$toast.success("All products received successfully! Transfer automatically completed.")
                    } catch (err) {
                        console.log("error in auto-completing transfer", err)
                        // Check if it's the stock ledger entry error
                        if (err.response?.data?.message?.includes('tabStock Ledger Entry') || 
                            err.response?.data?.message?.includes('cannot be null')) {
                            this.$toast.error("Products received successfully! Transfer completion failed due to stock ledger entry issue. Please contact support.")
                        } else {
                            this.$toast.success("Products received successfully! Please manually complete the transfer.")
                        }
                    }
                } else {
                    this.$toast.success("Products received successfully!")
                }
                
                this.$router.push(`/stock-transfers/${this.$route.params.id}`)
            } catch (err) {
                console.log("error in receiving products", err)
                this.$toast.error(errMsg(err, "Error in receiving products!"))
            }
            this.receivingProducts = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

/* Tokenize action bar spacing */
.mb-4 {
  margin-bottom: $sectionGap !important;
}

/* Right align numeric columns */
::deep(table) {
  thead th:nth-child(3),
  thead th:nth-child(4),
  thead th:nth-child(5) {
    text-align: right;
  }
  tbody td:nth-child(3),
  tbody td:nth-child(4),
  tbody td:nth-child(5) {
    text-align: right;
  }
}

/* Compact footer actions with token gap */
.d-flex.justify-content-end {
  gap: $sectionGap;
}
</style>
